export type Region = 'eu' | 'uk' | 'us' | 'il';

function domainOnly(input: string): string {
  try {
    return new URL(input.startsWith('http') ? input : `https://${input}`).hostname;
  } catch {
    return input;
  }
}

export function defaultCmpConfigForRegion(region: Region, domain: string) {
  const host = domainOnly(domain);
  const bannerBody =
    `${host} uses a small set of technologies that are strictly necessary to make the site work. ` +
    `Non-essential cookies and trackers stay off until you opt in. See our Cookie Policy for the full disclosure.`;

  const categories = [
    {
      id: 'essential',
      label: 'Essential / Strictly Necessary',
      icon: '🔒',
      colorKey: 'essential' as const,
      shortDesc: 'Required for the website to function. Cannot be disabled.',
      longDesc:
        'These cookies and similar technologies are necessary for the site to work: dismissing this banner, maintaining your session if you sign in, protecting form submissions against cross-site request forgery.',
      legalBasis: 'Art. 5(3) ePrivacy. No consent required.',
      alwaysOn: true,
      vendors: ['Cookie consent record (localStorage)', 'CSRF token (session)'],
      transfers: null,
      retention: 'Session or up to 1 year for security tokens.',
    },
    {
      id: 'functional',
      label: 'Functional',
      icon: '⚙️',
      colorKey: 'functional' as const,
      shortDesc: 'Enhanced website functionality and personalisation.',
      longDesc:
        'Functional cookies remember preferences such as language, theme, or recently viewed items so the site feels personalised. They are not required for the site to work.',
      legalBasis: 'Consent (GDPR Art. 6(1)(a); ePrivacy Art. 5(3)).',
      alwaysOn: false,
      vendors: [],
      transfers: null,
      retention: 'Up to 12 months.',
    },
    {
      id: 'analytics',
      label: 'Analytics & Performance',
      icon: '📊',
      colorKey: 'analytics' as const,
      shortDesc: 'Aggregate measurement of usage to improve the product.',
      longDesc:
        'Analytics cookies and pixels measure how visitors use the site in aggregate: pages viewed, time on page, conversion paths. They do not identify you personally.',
      legalBasis: 'Consent (GDPR Art. 6(1)(a); ePrivacy Art. 5(3)).',
      alwaysOn: false,
      vendors: [],
      transfers: null,
      retention: 'Up to 26 months.',
    },
    {
      id: 'marketing',
      label: 'Marketing & Advertising',
      icon: '📣',
      colorKey: 'marketing' as const,
      shortDesc: 'Personalised advertising and cross-site tracking.',
      longDesc:
        'Marketing cookies allow advertisers and ad networks to build a profile of your interests and show you relevant ads on this site and across the web.',
      legalBasis: 'Consent (GDPR Art. 6(1)(a); ePrivacy Art. 5(3)).',
      alwaysOn: false,
      vendors: [],
      transfers: null,
      retention: 'Up to 13 months.',
    },
  ];

  const regionLabels: Record<Region, { supervisor: string; supervisorUrl: string }> = {
    eu: {
      supervisor: 'Lead supervisory authority in your EU member state',
      supervisorUrl: 'https://edpb.europa.eu/about-edpb/about-edpb/members_en',
    },
    uk: {
      supervisor: "UK Information Commissioner's Office (ICO)",
      supervisorUrl: 'https://ico.org.uk/',
    },
    us: {
      supervisor: 'California Privacy Protection Agency (CPPA)',
      supervisorUrl: 'https://cppa.ca.gov/',
    },
    il: {
      supervisor: 'Israeli Privacy Protection Authority (PPA)',
      supervisorUrl: 'https://www.gov.il/en/departments/the_privacy_protection_authority',
    },
  };

  return {
    companyName: host,
    companyLogoUrl: null,
    cookiePolicyUrl: `https://${host}/cookie-policy`,
    privacyNoticeUrl: `https://${host}/privacy-policy`,
    termsUrl: null,
    dataController: host,
    supervisoryAuthority: {
      name: regionLabels[region].supervisor,
      url: regionLabels[region].supervisorUrl,
    },
    version: '1.0',
    storageKey: 'wedpo_cmp_consent',
    logKey: 'wedpo_cmp_log',
    receiptRetentionMonths: 24,
    allowedOrigins: [`https://${host}`],
    text: {
      bannerHeading: 'We use essential cookies, and ask before anything else',
      bannerBody,
    },
    categories,
  };
}
